import { useEffect, useMemo, useRef, useState } from 'react';
import { extractFindTerms, findMatches } from './find';

export type FlatMatch = {
  /** Indice del segmento (es. 0 = titolo, 1.. = paragrafi/textarea). */
  segmentIndex: number;
  start: number;
  end: number;
  /** Posizione globale nell'ordine di documento. */
  index: number;
};

type Options = {
  /** Query "trova" iniziale (es. `?find=` riportato dalla vista): apre la barra. */
  initialQuery?: string;
};

/**
 * Stato condiviso della ricerca "trova nella pagina" su una sequenza di
 * segmenti di testo (in ordine di documento). Calcola le occorrenze letterali
 * e gestisce la navigazione avanti/indietro con wrap-around.
 *
 * Possiede anche la glue comune ai chiamanti: la scorciatoia Cmd/Ctrl+F, il
 * focus sull'input all'apertura, il seed da `initialQuery` e `close()`. Il
 * chiamante passa `inputRef` alla `FindBar` per il focus.
 *
 * Vista: `segments = [titolo, ...paragrafi]`.
 * Edit:  `segments = [titolo, contenuto]` (i valori delle due textarea).
 */
export function useInPageFind(segments: string[], options?: Options) {
  const initialQuery = options?.initialQuery;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo<FlatMatch[]>(() => {
    const terms = extractFindTerms(query);
    if (terms.length === 0) return [];
    const out: FlatMatch[] = [];
    segments.forEach((seg, segmentIndex) => {
      for (const m of findMatches(seg, terms)) {
        out.push({ segmentIndex, start: m.start, end: m.end, index: out.length });
      }
    });
    return out;
  }, [segments, query]);

  // Ad ogni cambio di query si riparte dalla prima occorrenza.
  useEffect(() => {
    setCurrentIndex(0);
  }, [query]);

  // Carry-over dalla vista: ?find= apre la barra ed evidenzia subito.
  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
      setOpen(true);
    }
  }, [initialQuery]);

  // Scorciatoia da tastiera: Cmd/Ctrl+F apre/focalizza la ricerca.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus all'apertura.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function close() {
    setOpen(false);
    setQuery('');
  }

  const total = matches.length;
  const clampedIndex = total === 0 ? 0 : Math.min(currentIndex, total - 1);

  function next() {
    if (total === 0) return;
    setCurrentIndex((i) => (Math.min(i, total - 1) + 1) % total);
  }
  function prev() {
    if (total === 0) return;
    setCurrentIndex((i) => (Math.min(i, total - 1) - 1 + total) % total);
  }

  return {
    open,
    setOpen,
    query,
    setQuery,
    matches,
    total,
    currentIndex: clampedIndex,
    currentMatch: total > 0 ? matches[clampedIndex] : null,
    next,
    prev,
    inputRef,
    close,
  };
}
